// Interior PDF page-count padding for A3 double-sided printing.
//
// The printer images each album's interior on A3 sheets, double-sided. One
// A3 sheet carries 4 album (A4) pages. So EACH album's TOTAL interior PDF
// page count (intro page + content pages + outro page) must be divisible by
// 4. When it isn't, the bindery needs blank filler pages appended to reach
// the next multiple of 4.
//
// This is a PRINT/BINDING concern ONLY. It must never influence pricing,
// `order.page_count`, or anything in the checkout/submit flow — blank filler
// pages are never charged to the customer (pricing is per content page).

/**
 * Total interior page count rounded UP to the next multiple of 4 for A3
 * double-sided printing. The total is `contentPages` plus one page each for
 * the intro/outro bookends when present. Returns the same total when it is
 * already divisible by 4 (adds 0); otherwise adds 1, 2, or 3.
 */
export function paddedInteriorPageCount(
  contentPages: number,
  hasIntro: boolean,
  hasOutro: boolean,
): number {
  const total = contentPages + (hasIntro ? 1 : 0) + (hasOutro ? 1 : 0);
  return roundUpToMultipleOf4(total);
}

/**
 * Number of blank filler pages to append (0–3) so the interior reaches the
 * next multiple of 4. Mirrors `paddedInteriorPageCount` from the same inputs.
 */
export function interiorBlankCount(
  contentPages: number,
  hasIntro: boolean,
  hasOutro: boolean,
): number {
  const total = contentPages + (hasIntro ? 1 : 0) + (hasOutro ? 1 : 0);
  return paddedInteriorPageCount(contentPages, hasIntro, hasOutro) - total;
}

/**
 * Round an already-known page count up to the next multiple of 4. Used by the
 * PDF assembler where the actual page count of the produced doc
 * (`pdfDoc.getPageCount()`) is the most robust source of truth.
 */
export function roundUpToMultipleOf4(total: number): number {
  const r = total % 4;
  return r === 0 ? total : total + (4 - r);
}
